import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import type { Category } from '@adornis/buchungsheld-db/entities/category.js';
import type { ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/popover/x-dropdown';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import '../../../buchungsheld/src/client/pages/registration/bh-page-registration-workshop/bh-dialog-add-filter.js';
import '../../../buchungsheld/src/client/pages/registration/bh-page-registration-workshop/bh-dialog-select-list.js';

export const getFilters = (data: {
  filter: ProductFilter;
  productTopics?: string[];
  availableTimeframes?: Timeframe[];
  availableCategories?: Category[];
  translation;
}) => {
  const timeframes = data.filter.timeframeIDs ?? [];
  const categories = data.filter.categoryIDs ?? [];
  const topics = data.filter.topics ?? [];
  const presences = data.filter.presences ?? [];
  const languages = data.filter.languages ?? [];
  const hasFreeSeats = data.filter.hasFreeSeats;

  const tfs = data.availableTimeframes?.filter(tf => timeframes.includes(tf._id));
  const tfsString =
    tfs
      ?.map(tf => {
        return `${tf.from?.toFormat('dd.LL.yyyy') ?? ''} - ${tf.till?.toFormat('dd.LL.yyyy') ?? ''}`;
      })
      .join(', ') ?? '';

  const cats = data.availableCategories?.filter(ca => categories.includes(ca._id));
  const catsStr = cats?.map(c => c.name).join(', ');

  const filters = [] as Array<{
    key: string;
    value;
    selectables;
    renderString;
    multi: boolean;
    inUse: boolean;
    text: string;
  }>;

  if (data.availableTimeframes && data.availableTimeframes.length > 0)
    filters.push({
      key: 'timeframeIDs',
      inUse: timeframes.length > 0,
      value: timeframes,
      text: tfsString,
      selectables: data.availableTimeframes?.map(it => it._id) ?? [],
      renderString: tfID => {
        if (!tfID) return '';
        const tf = data.availableTimeframes?.find(tf => tfID === tf._id);

        if (!tf) return '';
        if (!tf.from && !tf.till) return tf.name;
        if (tf.from && !tf.till) return `${tf.name} (${tf.from.toFormat('dd.LL.yyyy')})`;
        if (!tf.from && tf.till) return `${tf.name} (${tf.till.toFormat('dd.LL.yyyy')})`;
        return `${tf.name} (${tf.from.toFormat('dd.LL.yyyy')} - ${tf.till.toFormat('dd.LL.yyyy')})`;
      },
      multi: false,
    });

  if (data.availableCategories && data.availableCategories.length > 0)
    filters.push({
      key: 'categoryIDs',
      inUse: categories.length > 0,
      value: categories,
      text: catsStr,
      selectables: data.availableCategories?.map(it => it._id) ?? [],
      renderString: caID => {
        if (!caID) return '';
        const ca = data.availableCategories?.find(ca => caID === ca._id);
        if (!ca) return '';
        return ca.name;
      },
      multi: false,
    });

  if (data.productTopics && data.productTopics.length > 0)
    filters.push({
      key: 'topics',
      inUse: topics.length > 0,
      value: topics,
      text: topics.join(', '),
      selectables: data.productTopics,
      renderString: item => item,
      multi: false,
    });

  filters.push({
    key: 'presences',
    inUse: presences.length > 0,
    value: presences,
    text: presences.join(', ').replace('_', ' '),
    selectables: ['in_person', 'online', 'hybrid'],
    renderString: item => data.translation.translate('PRODUCT', 'presenceTypes', item),
    multi: true,
  });

  filters.push({
    key: 'languages',
    inUse: languages.length > 0,
    value: languages,
    text: languages.join(', '),
    selectables: ['de', 'en'],
    renderString: item => data.translation.translate('PRODUCT', 'languages', item),
    multi: false,
  });

  filters.push({
    key: 'hasFreeSeats',
    inUse: hasFreeSeats === true,
    value: [hasFreeSeats],
    text: '> 0',
    selectables: [true],
    renderString: fs => (fs ? 'freie Plätze' : '-----'),
    multi: false,
  });

  return filters;
};

@customElement('bh-filter-bar')
export class BhFilterBar extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);
  options: any;

  @property({ attribute: false }) allFilters!: any[];
  @property({ attribute: false }) alwaysVisible: string[] = [];

  override render() {
    const filters = this.allFilters.filter(f => f.inUse || this.alwaysVisible.includes(f.key));

    return html`
      <x-flex horizontal space="md" ${css({ alignItems: 'center', flexWrap: 'wrap' })}>
        <!-- text filter -->
        ${filters.map(fl => {
          const dropdown_ref = createRef<XDropdown>();
          return html`
            <tr-chip
              ${css({
                background: '#e9e9ec',
                color: 'black',
                cursor: 'pointer',
              })}
              @click=${() => (console.log('ok'), dropdown_ref.value?.toggle())}
            >
              <x-flex
                horizontal
                ${acss({
                  alignItems: 'center',
                  opacity: fl.inUse ? 1 : 0.4,
                  '&:hover': { opacity: 1 },
                  transition: 'opacity .2s',
                })}
              >
                ${this._translation.translate('FILTER', fl.key)} ${fl.inUse ? ':' : ''}
                <span
                  ${css({
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  })}
                  >${fl.text}</span
                >
                ${this.renderDropdownDialog(fl, dropdown_ref)}
              </x-flex>
            </tr-chip>
          `;
        })}

        <!-- spacer -->
        <x-flex ${css({ flexGrow: '1' })}></x-flex>

        <!-- edit -->
        <bh-dialog-add-filter
          .allFilters=${this.allFilters}
          @onSave=${e => this.updateFilter(e.detail.key, e.detail.value)}
        ></bh-dialog-add-filter>
      </x-flex>
    `;
  }

  renderDropdownDialog(options, dropdown_ref) {
    if (!options) {
      console.error('no options given');
      return nothing;
    }

    const valueListRef = createRef();
    return html`
      <x-dropdown ${ref(dropdown_ref)}>
        <x-button mode="icon" icon="arrow_drop_down"> Trigger Dropdown </x-button>
        <x-flex slot="dropdown-content">
          <x-card>
            <x-flex vertical crossaxis-start ${css({ gap: '7px' })}>
              <bh-dialog-select-list
                ${ref(valueListRef)}
                .selectables=${options.selectables}
                .renderString=${options.renderString}
                .value=${options.value}
                @onChange=${async e => {
                  const value = (valueListRef.value?.value?.length ?? 0) === 0 ? undefined : valueListRef.value.value;
                  this.updateFilter(options.key, value);
                }}
              ></bh-dialog-select-list>

              <!-- spacer -->
              <x-flex
                ${css({
                  height: '1px',
                  width: '100%',
                  background: 'lightgray',
                })}
              ></x-flex>

              <x-button
                mode="text"
                class="hover-text-button"
                icon="delete"
                @click=${async () => {
                  this.updateFilter(options.key, undefined);
                  dropdown_ref.value.close();
                }}
              >
                ${this._translation.translate('delete')}
              </x-button>
            </x-flex>
          </x-card>
        </x-flex>
      </x-dropdown>
    `;
  }

  updateFilter(key, value) {
    this.dispatchEvent(new CustomEvent('onSave', { detail: { key, value } }));
  }
}
